/**
 * AI control-plane settings — Supabase backed (Phase 2C).
 *
 * The admin console never mutates code — it mutates this store, which is the
 * single runtime overlay on top of the build-time `aiConfig`.
 *
 * Persistence: `public.ai_platform_settings` (one shared row, key = 'default').
 * There is no localStorage cache any more: the database is the only source of
 * truth, so two admins always see the same configuration. Writes are
 * optimistic — the overlay applies immediately and rolls back if the row
 * cannot be persisted. Realtime keeps every open console in sync.
 */

import { supabase } from '@/integrations/supabase/client';

import { aiConfig } from './config';
import type { AICapability, AIProviderName } from './types';

export const SETTINGS_TABLE = 'ai_platform_settings';
export const SETTINGS_VERSIONS_TABLE = 'ai_platform_settings_versions';
export const SETTINGS_ROW_KEY = 'default';

export type SubscriptionPlan = 'free' | 'pro' | 'advanced';

export interface AIQuotaLimits {
  textImprovements: number;
  chatMessages: number;
  imageRequests: number;
}

export interface AIBudgetSettings {
  currency: string;
  dailyBudget: number | null;
  monthlyBudget: number | null;
  /** Percentage of budget that triggers a warning. */
  softThresholdPct: number;
  /** Percentage of budget that triggers a hard stop. */
  hardThresholdPct: number;
  /** Keep moderation running even after a hard stop. */
  keepModerationOnHardStop: boolean;
}

export interface AITelemetrySettings {
  enabled: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  /** Sample rate 0..1 for request-level telemetry. */
  sampleRate: number;
}

export interface AIPlatformSettings {
  /** Overrides `aiConfig.activeProvider` when set. */
  activeProvider: AIProviderName | null;
  fallbackProvider: AIProviderName | null;
  /** Per-capability provider assignment. */
  capabilityProviders: Partial<Record<AICapability, AIProviderName>>;
  /** Per-capability model, keyed `${provider}:${capability}`. */
  capabilityModels: Record<string, string>;
  /** Feature id → enabled. Absent = inherit the feature-flag default. */
  featureOverrides: Record<string, boolean>;
  budget: AIBudgetSettings;
  quotas: Record<SubscriptionPlan, AIQuotaLimits>;
  telemetry: AITelemetrySettings;
  updatedAt?: string;
  version?: number;
}

export interface AISettingsVersion {
  version: number;
  settings: Partial<AIPlatformSettings>;
  created_at: string;
  author_id: string | null;
  author_email: string | null;
  reason: string | null;
}

export const defaultAISettings = (): AIPlatformSettings => ({
  activeProvider: null,
  fallbackProvider: null,
  capabilityProviders: {},
  capabilityModels: {},
  featureOverrides: {},
  budget: {
    currency: 'USD',
    dailyBudget: null,
    monthlyBudget: null,
    softThresholdPct: 80,
    hardThresholdPct: 100,
    keepModerationOnHardStop: true,
  },
  quotas: {
    free: { textImprovements: 5, chatMessages: 20, imageRequests: 0 },
    pro: { textImprovements: 50, chatMessages: 200, imageRequests: 20 },
    advanced: { textImprovements: 500, chatMessages: 2000, imageRequests: 200 },
  },
  telemetry: { enabled: false, logLevel: 'info', sampleRate: 1 },
});

export const modelKey = (provider: AIProviderName, capability: AICapability) =>
  `${provider}:${capability}`;

type Listener = (settings: AIPlatformSettings) => void;

/** Loose view of the client — these tables are not in the generated types yet. */
interface SupabaseLike {
  from(table: string): {
    select(columns: string): {
      eq(column: string, value: string): {
        maybeSingle(): Promise<{ data: SettingsRow | null; error: unknown }>;
        order(
          column: string,
          options: { ascending: boolean },
        ): {
          limit(count: number): Promise<{ data: AISettingsVersion[] | null; error: unknown }>;
        };
      };
    };
    update(values: unknown): {
      eq(column: string, value: string): {
        select(columns: string): Promise<{ data: SettingsRow[] | null; error: unknown }>;
      };
    };
    upsert(
      values: unknown,
      options: { onConflict: string },
    ): {
      select(columns: string): Promise<{ data: SettingsRow[] | null; error: unknown }>;
    };
  };
  channel(name: string): {
    on(event: string, filter: unknown, cb: (payload: { new?: SettingsRow }) => void): {
      subscribe(): unknown;
    };
  };
  removeChannel(channel: unknown): void;
}

interface SettingsRow {
  settings?: unknown;
  updated_at?: string;
  version?: number;
}

const db = supabase as never as SupabaseLike;

class AISettingsStore {
  private settings: AIPlatformSettings = defaultAISettings();
  private listeners = new Set<Listener>();
  private loaded = false;
  private realtimeChannel: unknown = null;

  get(): AIPlatformSettings {
    return this.settings;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Load the shared row from Supabase. */
  async load(): Promise<AIPlatformSettings> {
    try {
      const { data, error } = await db
        .from(SETTINGS_TABLE)
        .select('settings, updated_at, version')
        .eq('key', SETTINGS_ROW_KEY)
        .maybeSingle();
      if (!error && data) this.adopt(data);
    } catch {
      /* offline / not an admin — keep built-in defaults */
    }
    this.loaded = true;
    this.apply();
    this.emit();
    return this.settings;
  }

  /**
   * Optimistically merge a patch, then persist. On failure the previous
   * settings are restored and the error is re-thrown for the caller to toast.
   */
  async update(patch: Partial<AIPlatformSettings>): Promise<AIPlatformSettings> {
    const previous = this.settings;
    const next = merge(previous, patch);
    next.updatedAt = new Date().toISOString();

    this.settings = next;
    this.apply();
    this.emit();

    try {
      const payload = { settings: stripMeta(next), updated_at: next.updatedAt };
      let { data, error } = await db
        .from(SETTINGS_TABLE)
        .update(payload)
        .eq('key', SETTINGS_ROW_KEY)
        .select('settings, updated_at, version');
      if (error) throw error;
      // An UPDATE that matches no row (missing seed row, or an RLS policy that
      // filters the row out) silently returns zero rows — never treat that as
      // a successful save. Try to create the row, then fail loudly.
      if (!data || data.length === 0) {
        ({ data, error } = await db
          .from(SETTINGS_TABLE)
          .upsert({ key: SETTINGS_ROW_KEY, ...payload }, { onConflict: 'key' })
          .select('settings, updated_at, version'));
        if (error) throw error;
        if (!data || data.length === 0) {
          throw new Error('AI settings were not saved (no row updated — check admin permissions)');
        }
      }
      if (data[0]) {
        this.settings.version = data[0].version ?? this.settings.version;
        this.settings.updatedAt = data[0].updated_at ?? this.settings.updatedAt;
      }
    } catch (error) {
      // rollback
      this.settings = previous;
      this.apply();
      this.emit();
      throw error instanceof Error ? error : new Error('Failed to save AI settings');
    }


    return this.settings;
  }

  async reset(): Promise<AIPlatformSettings> {
    return this.update(defaultAISettings());
  }

  /** Version history, newest first. */
  async listVersions(limit = 10): Promise<AISettingsVersion[]> {
    try {
      const { data } = await db
        .from(SETTINGS_VERSIONS_TABLE)
        .select('version, settings, created_at, author_id, author_email, reason')
        .eq('settings_key', SETTINGS_ROW_KEY)
        .order('version', { ascending: false })
        .limit(limit);
      return data ?? [];
    } catch {
      return [];
    }
  }

  /** Live-sync with other admins. Returns an unsubscribe function. */
  subscribeRealtime(): () => void {
    if (this.realtimeChannel) return () => this.unsubscribeRealtime();
    try {
      const channel = db
        .channel('ai-platform-settings')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: SETTINGS_TABLE },
          (payload) => {
            if (payload.new) {
              this.adopt(payload.new);
              this.apply();
              this.emit();
            }
          },
        );
      channel.subscribe();
      this.realtimeChannel = channel;
    } catch {
      /* realtime unavailable — polling in the console still refreshes */
    }
    return () => this.unsubscribeRealtime();
  }

  private unsubscribeRealtime(): void {
    if (!this.realtimeChannel) return;
    try {
      db.removeChannel(this.realtimeChannel);
    } catch {
      /* noop */
    }
    this.realtimeChannel = null;
  }

  private adopt(row: SettingsRow): void {
    this.settings = merge(defaultAISettings(), (row.settings ?? {}) as Partial<AIPlatformSettings>);
    this.settings.updatedAt = row.updated_at ?? undefined;
    this.settings.version = row.version ?? undefined;
  }

  /** Project the overlay onto the live `aiConfig` used by AIService. */
  apply(): void {
    const s = this.settings;
    if (s.activeProvider) aiConfig.activeProvider = s.activeProvider;
    aiConfig.fallbackProvider = s.fallbackProvider ?? aiConfig.fallbackProvider;
    aiConfig.capabilityProviders = { ...s.capabilityProviders };

    for (const [key, model] of Object.entries(s.capabilityModels)) {
      const [provider, capability] = key.split(':') as [AIProviderName, AICapability];
      if (!provider || !capability || !model) continue;
      aiConfig.defaultModels[provider] = {
        ...(aiConfig.defaultModels[provider] ?? {}),
        [capability]: model,
      };
    }
  }

  private emit(): void {
    this.listeners.forEach((l) => l(this.settings));
  }
}

function stripMeta(settings: AIPlatformSettings): Omit<AIPlatformSettings, 'updatedAt' | 'version'> {
  const rest: AIPlatformSettings = { ...settings };
  delete rest.updatedAt;
  delete rest.version;
  return rest;
}

function merge(base: AIPlatformSettings, patch: Partial<AIPlatformSettings>): AIPlatformSettings {
  return {
    ...base,
    ...patch,
    capabilityProviders: { ...base.capabilityProviders, ...(patch.capabilityProviders ?? {}) },
    capabilityModels: { ...base.capabilityModels, ...(patch.capabilityModels ?? {}) },
    featureOverrides: { ...base.featureOverrides, ...(patch.featureOverrides ?? {}) },
    budget: { ...base.budget, ...(patch.budget ?? {}) },
    telemetry: { ...base.telemetry, ...(patch.telemetry ?? {}) },
    quotas: {
      free: { ...base.quotas.free, ...(patch.quotas?.free ?? {}) },
      pro: { ...base.quotas.pro, ...(patch.quotas?.pro ?? {}) },
      advanced: { ...base.quotas.advanced, ...(patch.quotas?.advanced ?? {}) },
    },
  };
}

export const aiSettings = new AISettingsStore();
